import { BlobReader, BlobWriter, ZipReader, configure } from "@zip.js/zip.js";
import { api } from "../../lib/api";

configure({ useWebWorkers: false });
const IMG = /\.(jpe?g|png|webp)$/i;
const B = "/studio/photo-jobs";
export const BATCH = 250;
const SINGLE = 4500000;
const MAX = 15 * 1024 * 1024;
const PAR = 4;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
const fingerprint = (s) => { let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); };

// network errors, 429 and 5xx are retried with backoff; other errors go to the caller
async function call(fn) {
  for (let a = 0; ; a++) {
    try { return await fn(); } catch (e) {
      const s = e?.response?.status;
      if ((s && s !== 429 && s < 500) || a >= 6) throw e;
      await sleep(Math.min(1000 * 2 ** (a + 1), 60000));
    }
  }
}

async function pool(list, n, fn) {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(n, list.length) }, async () => { while (i < list.length) { const k = i++; await fn(list[k], k); } }));
}

export async function sendFile(jobId, file) {
  const { data: u } = await api.post(`${B}/${jobId}/files`, { file_name: file.name, size: file.size });
  const have = new Set(u.received);
  for (let n = 0; n < u.total_chunks; n++) {
    if (have.has(n)) continue;
    const blob = file.slice(n * u.chunk_size, Math.min(file.size, (n + 1) * u.chunk_size));
    await call(() => api.put(`${B}/uploads/${u.upload_id}/chunks/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }));
  }
  await call(() => api.post(`${B}/uploads/${u.upload_id}/complete`));
}

/** Lists photos in loose files, folders and ZIPs. ZIPs are read in place in the browser (never uploaded whole). */
export async function discover(fileList) {
  const items = [];
  const readers = [];
  for (const f of fileList) {
    const rel = f.webkitRelativePath || f.name;
    if (IMG.test(f.name)) items.push({ key: `${rel}|${f.size}|${f.lastModified}`, name: f.name, size: f.size, get: async () => f });
    else if (/\.zip$/i.test(f.name)) {
      const zr = new ZipReader(new BlobReader(f));
      readers.push(zr);
      for (const e of await zr.getEntries()) {
        const n = e.filename.split(/[\\/]/).pop();
        if (e.directory || !n || n.startsWith(".") || e.filename.startsWith("__MACOSX") || !IMG.test(n)) continue;
        items.push({ key: `${rel}::${e.filename}|${e.uncompressedSize}`, name: n, size: e.uncompressedSize,
          get: async () => { if (e.uncompressedSize > MAX) throw new Error("Image must be under 15MB"); return e.getData(new BlobWriter()); } });
      }
    }
  }
  return { items, close: () => readers.forEach((r) => r.close().catch(() => {})) };
}

async function sendOne(job, x) {
  const blob = await x.it.get();
  if (blob.size > MAX) throw new Error("Image must be under 15MB");
  if (blob.size <= SINGLE) {
    return call(() => api.put(`${B}/${job}/image`, blob, { params: { name: x.it.name, sha256: x.sha }, headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }));
  }
  return sendFile(job, new File([blob], x.it.name));
}

const reason = (e) => e?.response?.data?.detail || e?.message || "Upload failed";

/** Fingerprint -> ask MIVO what it already has -> transfer only new photos, 250 per job, 4 at a time. Resumable. */
export async function runUpload(items, label, onStat, ctl) {
  const ck = `mivo-photo-up:${items.length}:${fingerprint(items.map((i) => i.key).join("\n"))}`;
  const state = JSON.parse(localStorage.getItem(ck) || "null") || { batch: 0, open: null, sent: [], jobs: [] };
  const save = () => localStorage.setItem(ck, JSON.stringify(state));
  const batches = Math.ceil(items.length / BATCH);
  const st = { discovered: items.length, totalBytes: items.reduce((s, i) => s + i.size, 0), bytes: items.slice(0, state.batch * BATCH).reduce((s, i) => s + i.size, 0),
    checked: 0, sent: 0, reused: 0, attached: 0, pending: 0, failed: 0, batch: state.batch, batches, jobs: [...state.jobs], failures: [], resumed: state.batch > 0 || !!state.open };
  const emit = () => onStat({ ...st });
  const fail = (it, e) => { st.failed++; st.failures.push({ file: it.name, source: it.key, error: reason(e) }); };
  emit();
  for (let b = state.batch; b < batches && !ctl.stop; b++) {
    st.batch = b;
    const part = items.slice(b * BATCH, (b + 1) * BATCH).filter((it) => !state.sent.includes(it.key));
    const ok = [];
    await pool(part, PAR, async (it, i) => {
      try { const blob = await it.get(); ok[i] = { it, sha: hex(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer())) }; } catch (e) { fail(it, e); st.bytes += it.size; }
      st.checked++; emit();
    });
    const list = ok.filter(Boolean);
    let job = state.open;
    if (job) { try { if ((await api.get(`${B}/${job}`)).data.stage !== "uploading") job = null; } catch { job = null; } }
    if (list.length) {
      const { data } = await call(() => api.post(`${B}/check`, { items: list.map((x) => ({ name: x.it.name, sha256: x.sha, size: x.it.size })) }));
      const send = [], reuse = [];
      data.items.forEach((r, i) => {
        if (r.action === "upload") send.push(list[i]); else if (r.action === "reuse") reuse.push(list[i]);
        else { st[r.action === "skip_attached" ? "attached" : "pending"]++; st.bytes += list[i].it.size; }
      });
      if (send.length + reuse.length) {
        if (!job) {
          job = (await call(() => api.post(B, { note: `${label} · part ${b + 1}/${batches}`, expected_files: send.length + reuse.length, expected_bytes: [...send, ...reuse].reduce((s, x) => s + x.it.size, 0) }))).data.job_id;
          state.open = job; save();
        }
        if (reuse.length) {
          const { data: r } = await call(() => api.post(`${B}/${job}/reuse`, { items: reuse.map((x) => ({ name: x.it.name, sha256: x.sha, size: x.it.size })) }));
          for (const x of reuse) { if (r.missing.includes(x.it.name)) send.push(x); else { st.reused++; st.bytes += x.it.size; } }
        }
        await pool(send, PAR, async (x) => {
          if (ctl.stop) return;
          try { await sendOne(job, x); st.sent++; state.sent.push(x.it.key); save(); } catch (e) { fail(x.it, e); }
          st.bytes += x.it.size; emit();
        });
      }
    }
    if (ctl.stop) break;
    if (job) { await call(() => api.post(`${B}/${job}/start`)).catch(() => {}); state.jobs.push(job); st.jobs.push(job); }
    Object.assign(state, { batch: b + 1, open: null, sent: [] }); save();
    st.batch = b + 1; emit();
  }
  if (!ctl.stop) localStorage.removeItem(ck);
  return st;
}

export function failuresCsv(failures) {
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = ["file,source,error", ...failures.map((f) => [f.file, f.source, f.error].map(q).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
  a.download = "mivo_photo_upload_failures.csv";
  a.click();
}
